import { api } from './api'
import { notifySeriesChanged } from './seriesEvents'
import type { Batch, BatchDetail, BatchJob, BatchArchive, EditorConfig } from './types'

export interface SeriesJob extends BatchJob {
  batch_id?: string | null
  batch_title?: string | null
  completed_chunks?: number | null
  total_chunks?: number | null
  refund_confirmed?: boolean
  refund_gems?: number
  thumbnail_state?: string
  music_id?: number | null
  music_name?: string | null
  clip_packs?: { id: number; name: string }[]
  style?: string | null
  retryable?: boolean
}
export interface SeriesMetadata {
  display_name?: string | null
  music_id?: number | null
  music_name?: string | null
  clip_packs?: { id: number; name: string }[]
  styles?: { name: string; count: number }[]
  trim_start_sec?: number | null
  duration_sec?: number | null
  gems_charged?: number
  refund_gems?: number
  refund_pending?: number
  deletable?: boolean
  source_job_id?: string | null
  mean_completed_seconds?: number | null
  hook_collection_name?: string | null
  retention_days?: number
}
export interface Series extends Batch, SeriesMetadata { archive?: BatchArchive; members?: SeriesJob[] }
export interface SeriesDetail extends BatchDetail, SeriesMetadata { jobs: SeriesJob[] }
export type EditFilters = { status?: string; music_id?: string; pack_id?: string; style?: string }
export type EditStatusCounts = Record<'queued' | 'rendering' | 'done' | 'failed', number>
/** `status_counts` = edits per status AFTER the other filters, BEFORE the status one
 *  (MAPA 09, decyzja 12) — the chip says what a click on it will show. */
export interface SeriesEditsPage { items: SeriesJob[]; total: number; limit: number; offset: number; status_counts?: EditStatusCounts }
export interface ArchivePlan {
  archive: BatchArchive
  size_bytes?: number
  files?: { name: string; size_bytes: number; start: number; end: number }[]
}
/**
 * Ile editów niesie wiersz karty serii (`members`) — LUSTRO `CARD_MEMBERS`
 * z `app/render/series_view.py`. Pasek miniatur i szkielet liczą kadry z tej liczby,
 * bo pasek na sześć miejsc pod cztery przysłane kadry to pustka, nie projekt.
 * Rozjazd między tą stałą a backendem pilnuje `series.test.ts`.
 */
export const CARD_MEMBERS = 4
/** Mirrors zip_stream.MAX_ZIP_BYTES; unknown or larger selections cannot form an archive. */
export const MAX_ARCHIVE_BYTES = 2 * 1024 * 1024 * 1024
export function fetchSeriesEdits(filters: EditFilters, offset = 0) {
  const params = new URLSearchParams({ limit: '24', offset: String(offset) })
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value)
  return api<SeriesEditsPage>(`/api/batches/edits?${params}`)
}
export const archivePath = (ids: string[]) => `/api/batches/zip?ids=${ids.map(encodeURIComponent).join(',')}`
/** One ZIP containing exactly these ready edits. IDs are `SeriesJob.id` render-job IDs. */
export const selectedEditsArchivePath = (jobIds: string[]) => `/api/batches/zip?jobs=${jobIds.map(encodeURIComponent).join(',')}`
/**
 * Cała seria z WSZYSTKIMI editami (`read_series`), nie cztery kadry karty — podgląd
 * otwarty z paska serii liczy „edit {a} z {b}” po serii i chodzi strzałkami po serii
 * (MAPA 07). Osobno od `fetchBatch`, bo tamta zwraca `BatchDetail` bez pól serii.
 */
export const fetchSeries = (id: string) => api<SeriesDetail>(`/api/batches/${encodeURIComponent(id)}`)
export const fetchArchivePlan = (ids: string[]) => api<ArchivePlan>(`/api/batches/archive?ids=${ids.map(encodeURIComponent).join(',')}`)
export const removeSeries = (ids: string[]) => api<{ deleted_series: number; deleted_edits: number }>('/api/batches/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }).then(r => { notifySeriesChanged(); return r })
export const fetchSeriesConfig = (id: string) => api<{ editor_config: EditorConfig; title?: string | null; requested_count?: number }>(`/api/batches/${encodeURIComponent(id)}/config`)
export const retryFailedSeries = (id: string, idempotencyKey: string) => api<{ batch_id: string }> (`/api/batches/${encodeURIComponent(id)}/retry-failed`, { method: 'POST', body: JSON.stringify({ idempotency_key: idempotencyKey }) }).then(r => { notifySeriesChanged(); return r })
